import { Stack } from "@mui/material";
import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import type { ClientInfoExtended, ReportTemplate } from "../../../../../../../api/types/svTypes.ts";
import {
  getEditReportTemplateUrl,
  getOrganizationReportTemplatesUrl,
} from "../../../../report-lists/reports/utilities/editReportUrl.ts";
import SaveTemplateDialog from "../../../../report-lists/templates/SaveTemplateDialog.tsx";
import { useBiClientContext } from "../../../contexts/ClientContext.ts";
import useCurrentReportConfiguration from "../../../hooks/useCurrentReportConfiguration";
import { selectCurrentReport, selectIsReportChanged } from "../../../store/selectors/currentReportSelectors";
import UnsavedChangesDialog from "../../builder/backToReports/UnsavedChangesDialog";
import BackToButton from "../../common/BackToButton";
import ReportProductName from "../../common/ReportProductName.tsx";

interface Props {
  companies: ClientInfoExtended[];
}
export default function BackToTemplates({ companies }: Props) {
  const { clientInfo } = useBiClientContext();
  const navigate = useNavigate();

  const isReportChanged = useSelector(selectIsReportChanged);
  const template = useSelector(selectCurrentReport) as ReportTemplate;
  const { getCurrentReportConfigurationAsync } = useCurrentReportConfiguration();

  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);

  const navigateToTemplates = useCallback(() => {
    window.location.href = getOrganizationReportTemplatesUrl(clientInfo.organization);
  }, [clientInfo.organization]);

  const onBackButtonClicked = useCallback(() => {
    if (isReportChanged === true) {
      setShowUnsavedDialog(true);
    } else {
      navigateToTemplates();
    }
  }, [isReportChanged, navigateToTemplates, setShowUnsavedDialog]);

  const handleOnCancel = useCallback(() => setShowUnsavedDialog(false), [setShowUnsavedDialog]);
  const handleOnContinue = useCallback(() => navigateToTemplates(), [navigateToTemplates]);
  const handleOnSave = useCallback(() => {
    setShowSaveDialog(true);
    setShowUnsavedDialog(false);
  }, [setShowSaveDialog, setShowUnsavedDialog]);

  return (
    <>
      <Stack
        direction="row"
        useFlexGap
        spacing={1.4}
        sx={{
          flexWrap: "nowrap",
          alignItems: "center",
          flexBasis: 1,
          width: "100%",
        }}
      >
        <BackToButton ProductName={<ReportProductName title="Reporting" />} onClick={onBackButtonClicked} />
      </Stack>
      {showUnsavedDialog && (
        <UnsavedChangesDialog
          onCancel={handleOnCancel}
          onContinue={handleOnContinue}
          onSave={handleOnSave}
          name={template.name}
          type="template"
        />
      )}
      {showSaveDialog && template && (
        <SaveTemplateDialog
          title="Save Template"
          template={template}
          getTemplateConfigurationAsync={getCurrentReportConfigurationAsync}
          onClose={() => setShowSaveDialog(false)}
          onSaved={(rt) => {
            setShowSaveDialog(false);
            navigate(getEditReportTemplateUrl(rt.organization, rt.dataSource || clientInfo.clientCode, rt.reportId));
          }}
          companies={companies}
        />
      )}
    </>
  );
}
